import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAnimationConfig, useReducedMotion } from '../lib/animations/context';

interface LoadingProgressIndicatorProps {
  isVisible?: boolean;
  progress?: number;
  position?: 'top' | 'bottom';
  height?: number;
  color?: string;
  className?: string;
}

const LoadingProgressIndicator: React.FC<LoadingProgressIndicatorProps> = ({
  isVisible,
  progress,
  position = 'top',
  height = 3,
  color = '#FF5A1F',
  className = ''
}) => {
  const location = useLocation();
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const [internalProgress, setInternalProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Auto-trigger on route changes if no external control
  useEffect(() => {
    if (isVisible === undefined) {
      setIsLoading(true);
      setInternalProgress(0);

      // Simulate loading progress
      const progressInterval = setInterval(() => {
        setInternalProgress(prev => {
          const increment = Math.random() * 15;
          const newProgress = prev + increment;
          
          if (newProgress >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          
          return newProgress;
        });
      }, 100);

      // Complete loading after a delay
      const completeTimer = setTimeout(() => {
        setInternalProgress(100);
        
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }, 800 + Math.random() * 400);

      return () => {
        clearInterval(progressInterval);
        clearTimeout(completeTimer);
      };
    }
  }, [location.pathname, isVisible]);

  const currentProgress = progress !== undefined ? progress : internalProgress;
  const showIndicator = isVisible !== undefined ? isVisible : isLoading;

  const progressVariants = {
    initial: { 
      width: '0%',
      opacity: 0
    },
    animate: { 
      width: `${currentProgress}%`,
      opacity: 1,
      transition: {
        width: {
          duration: isReducedMotion ? 0 : 0.3,
          ease: 'easeOut'
        },
        opacity: {
          duration: 0.2
        }
      }
    },
    complete: {
      width: '100%',
      opacity: 1,
      transition: {
        duration: isReducedMotion ? 0 : 0.2,
        ease: 'easeOut'
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.3,
        delay: 0.2
      }
    }
  };

  const containerVariants = {
    initial: { 
      opacity: 0,
      y: position === 'top' ? -height : height
    },
    animate: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: isReducedMotion ? 0 : 0.2,
        ease: 'easeOut'
      }
    },
    exit: {
      opacity: 0,
      y: position === 'top' ? -height : height,
      transition: {
        duration: isReducedMotion ? 0 : 0.2,
        ease: 'easeIn'
      }
    }
  };

  const glowVariants = {
    animate: {
      boxShadow: [
        `0 0 0px ${color}`,
        `0 0 10px ${color}40`,
        `0 0 0px ${color}`
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  const positionClasses = {
    top: 'top-0',
    bottom: 'bottom-0'
  };

  return (
    <AnimatePresence>
      {showIndicator && (
        <motion.div
          variants={containerVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className={`fixed left-0 right-0 z-50 ${positionClasses[position]} ${className}`}
          style={{ height: `${height}px` }}
        >
          {/* Background */}
          <div className="w-full h-full bg-gray-200/50 backdrop-blur-sm" />
          
          {/* Progress Bar */}
          <motion.div
            variants={progressVariants}
            initial="initial"
            animate={currentProgress >= 100 ? "complete" : "animate"}
            className="absolute top-0 left-0 h-full"
            style={{ 
              background: `linear-gradient(90deg, ${color}, ${color}dd)`,
            }}
          >
            {/* Glow effect */}
            {!isReducedMotion && (
              <motion.div
                variants={glowVariants}
                animate="animate"
                className="absolute inset-0"
                style={{ backgroundColor: color }}
              />
            )}
            
            {/* Shimmer effect */}
            {!isReducedMotion && currentProgress < 100 && (
              <motion.div
                animate={{ x: ['0%', '100%'] }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'linear'
                }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                style={{ width: '30%' }}
              />
            )}
          </motion.div>

          {/* Completion flash */}
          {currentProgress >= 100 && !isReducedMotion && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: [0, 0.3, 0],
                transition: { duration: 0.5 }
              }}
              className="absolute inset-0 bg-white"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingProgressIndicator;